import { Button, Input, Spin, Modal, App as AntApp } from 'antd';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import { PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../../constants';
import { useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string';
import { selectAllDrivers } from '../../modules/driver/stores/slice';
import {
  doGetDrivers,
  doApproveDriver,
  doRejectDriver,
  doLockDriver,
  doCheckMissingDriverIds,
  doSeedDriverIds,
  doUpdateDriver,
} from '../../modules/driver/stores/thunk';
import DriversTable from '../Elements/Tables/DriversTable';
import DriversFilter from '../Elements/Filters/DriversFilter';
import { getEmailTemplates } from '../../modules/driver/api';

const DriversPage = () => {
  const dispatch = useDispatch();
  const { message } = AntApp.useApp();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [hasMissingIds, setHasMissingIds] = useState(false);
  const [isSeedingIds, setIsSeedingIds] = useState(false);
  const [emailModal, setEmailModal] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [searchParams] = useSearchParams();
  let location = useLocation();
  let navigate = useNavigate();
  const isFetching = useSelector((state) => state.driver.isFetching);
  const metadata = useSelector((state) => state.driver.metadata);
  const drivers = useSelector((state) => selectAllDrivers(state));

  const query = Object.fromEntries(searchParams);
  const currentPage = Number(query.current) || 1;
  const itemsPerPage = Number(query.pageSize) || PAGE_SIZE;

  const checkMissingIds = () => {
    dispatch(doCheckMissingDriverIds()).then((result) => {
      if (doCheckMissingDriverIds.fulfilled.match(result)) {
        setHasMissingIds(result.payload?.hasMissing || false);
      }
    });
  };

  useEffect(() => {
    const params = { ...query };
    dispatch(doGetDrivers({ page: currentPage, take: itemsPerPage, ...params }));
    setSearchValue(params.q || '');
  }, [currentPage, itemsPerPage, searchParams]);

  useEffect(() => {
    checkMissingIds();
  }, []);

  useEffect(() => {
    getEmailTemplates().then(({ response }) => {
      if (response) {
        setTemplates(Array.isArray(response) ? response : []);
      }
    });
  }, []);

  const pagination = {
    current: currentPage || 1,
    pageSize: itemsPerPage,
    locale: { items_per_page: 'Drivers/Page' },
    total: metadata.totalItems || drivers.length,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    showSizeChanger: true,
    showTotal: (total) => `${total} Drivers`,
  };

  const handleFilter = (params, updatePath) => {
    delete params.pageSizeOptions;
    delete params.showSizeChanger;
    delete params.total;
    delete params.locale;
    delete params.page;
    if (updatePath) {
      navigate({
        pathname: location.pathname,
        search: queryString.stringify(params || {}, {
          skipNull: true,
        }),
      });
    }
    const newCurrentPage = Number(params?.current || 1);
    const newItemsPerPage = Number(params?.pageSize) || PAGE_SIZE;
    dispatch(
      doGetDrivers({
        page: newCurrentPage,
        take: newItemsPerPage,
        ...params,
      }),
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch && handleSearch();
    }
  };

  const handleChangeSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const handleSearch = () => {
    handleFilter({ ...query, q: searchValue || '', current: 1 }, true);
  };

  const handleTableChange = (params) => {
    const newParams = { ...query, ...params };
    handleFilter(newParams, true);
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const openEmailModal = (id, type) => {
    const key = type === 'approve' ? 'driver_approve' : 'driver_reject';
    const tpl = templates.find((t) => t.key === key);
    setEmailModal({
      type,
      id,
      subject: tpl?.subject || '',
      body: tpl?.body || '',
    });
  };

  const handleApprove = (id) => openEmailModal(id, 'approve');

  const handleReject = (id) => openEmailModal(id, 'reject');

  const handleLock = (id) => {
    dispatch(doLockDriver(id)).then((result) => {
      if (doLockDriver.fulfilled.match(result)) {
        handleFilter({ ...query, current: currentPage, pageSize: itemsPerPage });
      }
    });
  };

  const submitEmailModal = () => {
    if (!emailModal) return;
    const action = emailModal.type === 'approve' ? doApproveDriver : doRejectDriver;
    dispatch(
      action({
        id: emailModal.id,
        email: { subject: emailModal.subject, body: emailModal.body },
      }),
    ).then((result) => {
      if (action.fulfilled.match(result)) {
        setEmailModal(null);
        handleFilter({ ...query, current: currentPage, pageSize: itemsPerPage });
      }
    });
  };

  const handleTogglePreferred = (id, checked) => {
    dispatch(doUpdateDriver({ id, isPreferred: checked })).then((result) => {
      if (doUpdateDriver.fulfilled.match(result)) {
        handleFilter({ ...query, current: currentPage, pageSize: itemsPerPage });
      }
    });
  };

  const handleToggleGstPayout = (id, checked) => {
    dispatch(doUpdateDriver({ id, gstPayoutEnabled: checked })).then((result) => {
      if (doUpdateDriver.fulfilled.match(result)) {
        handleFilter({ ...query, current: currentPage, pageSize: itemsPerPage });
      }
    });
  };

  const handleSeedDriverIds = () => {
    setIsSeedingIds(true);
    dispatch(doSeedDriverIds()).then((result) => {
      setIsSeedingIds(false);
      if (doSeedDriverIds.fulfilled.match(result)) {
        const updated = result.payload?.updated || 0;
        message.success(`Successfully created Driver IDs for ${updated} driver(s).`);
        checkMissingIds();
        handleFilter({ ...query, current: currentPage, pageSize: itemsPerPage });
      } else {
        message.error('Failed to create Driver IDs. Please try again.');
      }
    });
  };

  const withAction = (record) => {
    return (
      <div className="actions">
        <Link to={'/drivers/' + record.id}>
          <Button type="link" className="edit">
            <ReactSVG className="icon" src="/icons/edit.svg" />
          </Button>
        </Link>

        <Button type="link" className="more">
          <ReactSVG className="icon" src="/icons/more.svg" />
        </Button>
      </div>
    );
  };
  return (
    <Spin spinning={false}>
      <div className="Content DriversPage">
        <div className="ContentHeader">
          <div className="HeaderTitle">DRIVERS</div>
          <div className="HeaderToolBar">
            <div className="ToolBarButtons">
              {hasMissingIds && (
                <Button
                  className="ButtonWarning"
                  type="default"
                  size="small"
                  loading={isSeedingIds}
                  onClick={handleSeedDriverIds}
                  style={{
                    marginRight: 8,
                    backgroundColor: '#faad14',
                    borderColor: '#faad14',
                    color: '#fff',
                  }}
                >
                  Create Driver IDs
                  <ReactSVG className="icon" src="/icons/add.svg" />
                </Button>
              )}
              <Link to="/drivers/create">
                <Button className="CreateButton ButtonPrimary" type="primary" size="small">
                  Create New Driver
                  <ReactSVG className="icon" src="/icons/add.svg" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <div className="orders-header-table page-header-table">
          <div className="search">
            <Input
              placeholder="Search by name, email, driver ID"
              suffix={<img src="/icons/search.svg" />}
              onChange={handleChangeSearch}
              onBlur={handleSearch}
              onKeyDown={handleKeyDown}
              disabled={isFetching}
              value={searchValue}
            />
          </div>
          <div className="buttons">
            <Button className="FilterButton ButtonFilter" onClick={() => setIsFilterOpen(!isFilterOpen)}>
              Filter
              <ReactSVG className="icon" src="/icons/filter_list.svg" />
            </Button>
          </div>
        </div>
        {isFilterOpen && <DriversFilter isFetching={isFetching} query={query} onSubmit={handleFilter} />}
        <div className="ContentMain">
          <DriversTable
            dataSource={drivers}
            pagination={pagination}
            withAction={withAction}
            selectedRowKeys={selectedRowKeys}
            onSelectChange={onSelectChange}
            onApproveDriver={handleApprove}
            onRejectDriver={handleReject}
            onLockDriver={handleLock}
            onTogglePreferred={handleTogglePreferred}
            onToggleGstPayout={handleToggleGstPayout}
            rowKey="id"
            isFetching={false}
            onChange={handleTableChange}
          />
        </div>
      </div>
      <Modal
        title={emailModal?.type === 'approve' ? 'Approve Driver — Email' : 'Reject Driver — Email'}
        open={!!emailModal}
        onCancel={() => setEmailModal(null)}
        width={640}
        footer={null}
      >
        <div style={{ marginBottom: 8, color: '#888', fontSize: 13 }}>
          Available variables: <code>{'{{driverName}}'}</code>, <code>{'{{contactUrl}}'}</code>,{' '}
          <code>{'{{driveWithPkupUrl}}'}</code>, <code>{'{{termsUrl}}'}</code>
        </div>
        <Input
          value={emailModal?.subject || ''}
          onChange={(e) => setEmailModal((prev) => ({ ...prev, subject: e.target.value }))}
          placeholder="Subject"
          style={{ marginBottom: 12 }}
        />
        <Input.TextArea
          value={emailModal?.body || ''}
          onChange={(e) => setEmailModal((prev) => ({ ...prev, body: e.target.value }))}
          rows={12}
          placeholder="Email body"
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button onClick={() => setEmailModal(null)}>Cancel</Button>
          <Button type="primary" onClick={submitEmailModal}>
            Send
          </Button>
        </div>
      </Modal>
    </Spin>
  );
};

export default DriversPage;
